import { z } from 'zod';

export const IEQ2_PAST_12_MONTHS_TRADES_AMOUNT = [
  'no_trades',
  '1_5_trades',
  '5_10_trades',
  'more_than_10_trades',
] as const;

export const ZodIEQ2Past12MonthsTradesAmount = z.enum(IEQ2_PAST_12_MONTHS_TRADES_AMOUNT);

export type IEQ2Past12MonthsTradesAmount = z.infer<typeof ZodIEQ2Past12MonthsTradesAmount>;

export const IEQ2_PAST_12_MONTHS_INVESTED_AMOUNT = [
  'none',
  '0_5000',
  '5000_10000',
  'more_than_10000',
] as const;

export const ZodIEQ2Past12MonthsInvestedAmount = z.enum(IEQ2_PAST_12_MONTHS_INVESTED_AMOUNT);

export type IEQ2Past12MonthsInvestedAmount = z.infer<typeof ZodIEQ2Past12MonthsInvestedAmount>;

export const IEQ2_YES_NO = ['yes', 'no'] as const;
export const ZodIEQ2YesNo = z.enum(IEQ2_YES_NO);
export type IEQ2YesNo = z.infer<typeof ZodIEQ2YesNo>;

export const IEQ2_ANSWER = ['correct', 'incorrect', 'i_dont_know'] as const;
export const ZodIEQ2Answer = z.enum(IEQ2_ANSWER);
export type IEQ2Answer = z.infer<typeof ZodIEQ2Answer>;

export const ZodInvestingExperienceQuestionnaireV2 = z.object({
  general: z.object({
    finance_education: ZodIEQ2YesNo,
    finance_position: ZodIEQ2YesNo,
  }),
  etfs: z.object({
    trades_amount: ZodIEQ2Past12MonthsTradesAmount,
    invested_amount: ZodIEQ2Past12MonthsInvestedAmount.nullable(),
    definition_knowledge: ZodIEQ2Answer,
    return_guarantee_knowledge: ZodIEQ2Answer,
    fees_knowledge: ZodIEQ2Answer,
    high_return_low_risk_knowledge: ZodIEQ2Answer,
    market_impact_war_knowledge: ZodIEQ2Answer,
  }),
  stocks: z.object({
    trades_amount: ZodIEQ2Past12MonthsTradesAmount,
    invested_amount: ZodIEQ2Past12MonthsInvestedAmount.nullable(),
    ownership_interests_knowledge: ZodIEQ2Answer,
    dividends_knowledge: ZodIEQ2Answer,
    volatility_knowledge: ZodIEQ2Answer,
    issue_knowledge: ZodIEQ2Answer,
    bond_ownership_knowledge: ZodIEQ2Answer,
  }),
  commodities: z.object({
    trades_amount: ZodIEQ2Past12MonthsTradesAmount,
    invested_amount: ZodIEQ2Past12MonthsInvestedAmount.nullable(),
    definition_knowledge: ZodIEQ2Answer,
    capital_guarantee_knowledge: ZodIEQ2Answer,
    derivatives_knowledge: ZodIEQ2Answer,
    counterparty_risk_knowledge: ZodIEQ2Answer,
    volatility_knowledge: ZodIEQ2Answer,
    gold_storage_fees_knowledge: ZodIEQ2Answer,
  }),
  eltif: z.object({
    trades_amount: ZodIEQ2Past12MonthsTradesAmount,
    invested_amount: ZodIEQ2Past12MonthsInvestedAmount.nullable(),
    definition_knowledge: ZodIEQ2Answer,
    long_terms_knowledge: ZodIEQ2Answer,
    daily_liquidity_knowledge: ZodIEQ2Answer,
    monthly_redemption_knowledge: ZodIEQ2Answer,
    capital_recovery_knowledge: ZodIEQ2Answer,
    short_term_liquidity_knowledge: ZodIEQ2Answer,
  }),
  protected_products: z.object({
    trades_amount: ZodIEQ2Past12MonthsTradesAmount,
    invested_amount: ZodIEQ2Past12MonthsInvestedAmount.nullable(),
    definition_knowledge: ZodIEQ2Answer,
    early_redemption_knowledge: ZodIEQ2Answer,
    capital_guarantee_knowledge: ZodIEQ2Answer,
    conditional_coupons_knowledge: ZodIEQ2Answer,
    no_risk_knowledge: ZodIEQ2Answer,
    performance_impact_knowledge: ZodIEQ2Answer,
  }),
  guaranteed_products: z.object({
    trades_amount: ZodIEQ2Past12MonthsTradesAmount,
    invested_amount: ZodIEQ2Past12MonthsInvestedAmount.nullable(),
    definition_knowledge: ZodIEQ2Answer,
    full_repayment_knowledge: ZodIEQ2Answer,
    variable_return_knowledge: ZodIEQ2Answer,
    sensitivity_interest_rates_knowledge: ZodIEQ2Answer,
    credit_risk_knowledge: ZodIEQ2Answer,
    early_redemption_knowledge: ZodIEQ2Answer,
  }),
});

export type InvestingExperienceQuestionnaireV2 = z.infer<
  typeof ZodInvestingExperienceQuestionnaireV2
>;
